import { useEffect, useState } from "react";
import { LocalizedLink as Link } from "@/components/routing/LocalizedLink";
import { useLocalizedNavigate as useNavigate } from "@/hooks/useLocalizedNavigate";
import { z } from "zod";
import { Eye, EyeOff, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AuthSplitLayout } from "@/components/auth/AuthSplitLayout";
import { Seo } from "@/components/seo/Seo";

const passwordSchema = z.object({
  email: z.string().trim().email(),
  password: z.string().min(8),
});
const magicSchema = z.object({
  email: z.string().trim().email(),
});

function getNextParam(): string | null {
  if (typeof window === "undefined") return null;
  const raw = new URLSearchParams(window.location.search).get("next");
  if (!raw) return null;
  try {
    const decoded = decodeURIComponent(raw);
    // AUD-23: only allow same-origin internal paths under a supported locale.
    // Rejects protocol-relative, backslash, control chars, and missing locale prefix.
    // The `[a-z]{2}` allow-list is pinned to i18n.supportedLngs (en/fr/es/pt).
    if (!/^\/[a-z]{2}(\/|$)/.test(decoded)) return null;
    if (decoded.includes("\\") || /[\x00-\x1f]/.test(decoded)) return null;
    return decoded;
  } catch {
    return null;
  }
}

async function routeAfterAuth(navigate: (path: string, opts?: { replace: boolean }) => void) {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) {
    navigate("/login", { replace: true });
    return;
  }
  const { data: profile } = await supabase
    .from("profiles")
    .select("onboarding_completed_at")
    .eq("id", u.user.id)
    .maybeSingle();
  if (profile?.onboarding_completed_at) {
    const next = getNextParam();
    if (next) {
      // Use raw window.location to avoid double-locale prefixing
      window.location.replace(next);
      return;
    }
    navigate("/dashboard", { replace: true });
    return;
  }
  navigate("/onboarding/persona", { replace: true });
}

const Login = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tab, setTab] = useState<"password" | "magic">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [loading, setLoading] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session) void routeAfterAuth(navigate);
    });
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) void routeAfterAuth(navigate);
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = passwordSchema.safeParse({ email, password });
    if (!parsed.success) {
      toast.error(t("auth.validation.loginInvalid"));
      return;
    }
    setLoading(true);
    const { error } = await supabase.auth.signInWithPassword({ email: parsed.data.email, password });
    setLoading(false);
    if (error) {
      toast.error(t("auth.errors.invalidCredentials"));
      return;
    }
    toast.success(t("auth.toasts.welcomeBack"));
  };

  const handleMagicSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = magicSchema.safeParse({ email });
    if (!parsed.success) {
      toast.error(t("auth.validation.emailInvalid", "Enter a valid email"));
      return;
    }
    setLoading(true);
    const lang = (typeof window !== "undefined" ? window.location.pathname.match(/^\/([a-z]{2})(?=\/|$)/)?.[1] : null) || "en";
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data.email,
      options: {
        emailRedirectTo: `${window.location.origin}/${lang}/onboarding/persona`,
      },
    });
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setMagicSent(true);
    toast.success(t("auth.toasts.magicSent", "Check your inbox for the sign-in link"));
  };

  const handleGoogle = async () => {
    setLoading(true);
    const lang = (typeof window !== "undefined" ? window.location.pathname.match(/^\/([a-z]{2})(?=\/|$)/)?.[1] : null) || "en";
    const result = await lovable.auth.signInWithOAuth("google", { redirect_uri: `${window.location.origin}/${lang}` });
    if (result.error) {
      setLoading(false);
      toast.error(t("auth.errors.googleFailed"));
    }
  };

  return (
    <>
      <Seo title={t("auth.login.seoTitle")} description={t("auth.login.seoDescription")} />
      <AuthSplitLayout
        side={
          <div>
            <div className="t-eyebrow text-primary">{t("auth.panel.label")}</div>
            <h2 className="mt-3 t-display-lg leading-[1.05]">
              {t("auth.panel.headline")}
            </h2>
            <p className="mt-4 t-body text-white/75">{t("auth.panel.body")}</p>
            <div className="mt-8 grid grid-cols-3 gap-3">
              {[
                { n: "45", l: t("auth.panel.statBootcamps") },
                { n: "7", l: t("auth.panel.statTracks") },
                { n: "4", l: t("auth.panel.statContinents") },
              ].map((s) => (
                <div key={s.l} className="rounded-lg border border-white/15 bg-white/[0.08] p-4 backdrop-blur">
                  <div className="t-display-sm text-white">{s.n}</div>
                  <div className="mt-1 t-eyebrow text-white/70">{s.l}</div>
                </div>
              ))}
            </div>
            <blockquote className="mt-8 border-l-2 border-primary/60 pl-4 t-body-sm italic text-white/80">
              "{t("auth.panel.testimonial")}"
              <footer className="mt-2 t-eyebrow not-italic text-white/60">— {t("auth.panel.testimonialName")}</footer>
            </blockquote>
          </div>
        }
      >
        <div className="t-eyebrow text-primary">{t("auth.signIn", "Sign in")}</div>
        <h1 className="mt-2 t-display-md text-text-1">{t("auth.login.title")}</h1>
        <p className="mt-2 t-body-sm text-text-3">{t("auth.login.subtitle")}</p>

        <Button
          type="button"
          variant="brandOutline"
          size="lg"
          className="mt-6 w-full"
          onClick={handleGoogle}
          disabled={loading}
        >
          {t("auth.continueWithGoogle")}
        </Button>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border-1" /></div>
          <div className="relative flex justify-center"><span className="bg-surface-1 px-3 t-eyebrow text-text-3">{t("auth.or")}</span></div>
        </div>

        <Tabs value={tab} onValueChange={(v) => setTab(v as "password" | "magic")} className="w-full">
          <TabsList className="grid w-full grid-cols-2 rounded-full bg-surface-2 p-1">
            <TabsTrigger value="password" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{t("auth.login.tabs.password", "Password")}</TabsTrigger>
            <TabsTrigger value="magic" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{t("auth.login.tabs.magic", "Magic link")}</TabsTrigger>
          </TabsList>

          <TabsContent value="password" className="mt-4">
            <form onSubmit={handlePasswordSubmit} className="space-y-4" noValidate>
              <div className="space-y-2">
                <Label htmlFor="email" className="t-body-sm font-medium text-text-2">{t("auth.fields.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="bg-surface-2 border-border-1"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="t-body-sm font-medium text-text-2">{t("auth.fields.password")}</Label>
                  <Link
                    to="/auth/forgot-password"
                    className="t-body-sm font-medium text-primary hover:underline underline-offset-4"
                  >
                    {t("auth.login.forgot")}
                  </Link>
                </div>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-surface-2 border-border-1 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((s) => !s)}
                    aria-label={showPw ? t("auth.hidePassword") : t("auth.showPassword")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-text-3 hover:text-text-1"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox
                  id="remember"
                  checked={remember}
                  onCheckedChange={(v) => setRemember(v === true)}
                />
                <Label htmlFor="remember" className="t-body-sm font-normal text-text-2">
                  {t("auth.login.remember")}
                </Label>
              </div>
              <Button type="submit" variant="brand" size="xl" className="w-full" disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.login.submit")}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="magic" className="mt-4">
            {magicSent ? (
              <div className="rounded-lg border border-primary/20 bg-primary/[0.06] p-5">
                <div className="flex items-center gap-2 t-body font-semibold text-text-1">
                  <Mail className="h-4 w-4 text-primary" />
                  {t("auth.login.magic.sentTitle", "Check your inbox")}
                </div>
                <p className="mt-2 t-body-sm text-text-3">
                  {t("auth.login.magic.sentBody", "We sent a sign-in link to {{email}}. It expires in 1 hour.", { email })}
                </p>
                <Button
                  type="button"
                  variant="brandGhost"
                  size="sm"
                  className="mt-3 -ml-2"
                  onClick={() => setMagicSent(false)}
                >
                  {t("auth.login.magic.useDifferent", "Use a different email")}
                </Button>
              </div>
            ) : (
              <form onSubmit={handleMagicSubmit} className="space-y-4" noValidate>
                <div className="space-y-2">
                  <Label htmlFor="email-magic" className="t-body-sm font-medium text-text-2">{t("auth.fields.email")}</Label>
                  <Input
                    id="email-magic"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="bg-surface-2 border-border-1"
                  />
                </div>
                <p className="t-body-sm text-text-3">
                  {t("auth.login.magic.hint", "We'll email you a one-tap sign-in link. No password needed.")}
                </p>
                <Button type="submit" variant="brand" size="xl" className="w-full" disabled={loading}>
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("auth.login.magic.submit", "Email me a sign-in link")}
                </Button>
              </form>
            )}
          </TabsContent>
        </Tabs>

        <p className="mt-6 text-center t-body-sm text-text-3">
          {t("auth.login.noAccount")}{" "}
          <Link to="/signup" className="font-medium text-primary hover:underline underline-offset-4">
            {t("auth.login.signUpLink")}
          </Link>
        </p>
      </AuthSplitLayout>
    </>
  );
};

export default Login;
